import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQueries,
} from "@tanstack/react-query";
import {
  getMotionAmplificationJob,
  getMotionAmplificationJobs,
  type MotionAmplificationJob,
} from "../clients/motion-amplification";

const motionAmplificationJobsKey = ["motion-amplification", "jobs"] as const;
export const motionAmplificationJobsListKey = [
  ...motionAmplificationJobsKey,
  "list",
] as const;
export const motionAmplificationJobDetailKey = (
  jobId: MotionAmplificationJob["jobId"],
) => [...motionAmplificationJobsKey, "detail", jobId] as const;

const processingPollIntervalMs = 3000;
const jobsStaleTimeMs = 30_000;
const jobsPageSize = 100;

function isActiveStatus(status: MotionAmplificationJob["status"]) {
  return status === "Processing";
}

function shouldPollJob(status: MotionAmplificationJob["status"] | undefined) {
  return status === undefined || status === "Processing";
}

export function motionAmplificationJobQueryOptions(
  jobId: MotionAmplificationJob["jobId"],
) {
  return queryOptions({
    queryKey: motionAmplificationJobDetailKey(jobId),
    queryFn: () => getMotionAmplificationJob(jobId),
    refetchInterval: (query) =>
      shouldPollJob(query.state.data?.status)
        ? processingPollIntervalMs
        : false,
  });
}

export const motionAmplificationJobsQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: motionAmplificationJobsListKey,
    queryFn: ({ pageParam }) =>
      getMotionAmplificationJobs({ limit: jobsPageSize, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: jobsStaleTimeMs,
  });

export function useMotionAmplificationJobsQuery() {
  const jobsQuery = useInfiniteQuery(motionAmplificationJobsQueryOptions());
  const listedJobs = jobsQuery.data?.pages.flatMap((page) => page.jobs);
  const processingJobIds =
    listedJobs
      ?.filter((job) => isActiveStatus(job.status))
      .map((job) => job.jobId) ?? [];
  const processingJobQueries = useQueries({
    queries: processingJobIds.map(motionAmplificationJobQueryOptions),
  });
  const currentJobsById = new Map(
    processingJobQueries
      .map((query) => query.data)
      .filter((job) => job !== undefined)
      .map((job) => [job.jobId, job]),
  );
  const jobs = listedJobs?.map((job) => currentJobsById.get(job.jobId) ?? job);

  return { ...jobsQuery, data: jobs };
}
